import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/uk";
import { CurrencyExchange } from "react-bootstrap-icons";
import Loader from "../components/common/Loader";
import { AppCheckbox, AppMonthField } from "../components/form";

dayjs.locale("uk");

function formatMoney(n) {
    if (n == null || Number.isNaN(n)) return "—";
    return Math.round(Number(n)).toLocaleString("uk-UA");
}

/** Частка суми від доходу місяця (для додаткових колонок %). */
function formatPctOfIncome(partUah, incomeUah) {
    const inc = Number(incomeUah);
    if (!Number.isFinite(inc) || inc === 0) return "—";
    const part = Number(partUah);
    if (!Number.isFinite(part)) return "—";
    const p = (part / inc) * 100;
    if (!Number.isFinite(p)) return "—";
    return `${p.toLocaleString("uk-UA", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} %`;
}

/** Режим колонок на місяць за двома чекбоксами «Суми» / «%». */
function pnlMonthViewMode(showValues, showPct) {
    if (showValues && showPct) return "both";
    if (showValues && !showPct) return "sums";
    if (!showValues && showPct) return "pct";
    return "neither";
}

/** Індекс колонки суми (грн); у режимі «лише %» — null. */
function pnlMonthColAmount(mi, mode) {
    if (mode === "both") return mi * 2 + 1;
    if (mode === "pct") return null;
    return mi + 1;
}

/** Індекс колонки %; є лише в режимах both і pct. */
function pnlMonthColPct(mi, mode) {
    if (mode === "both") return mi * 2 + 2;
    if (mode === "pct") return mi + 1;
    return null;
}

function colsPerMonthForMode(mode) {
    return mode === "both" ? 2 : 1;
}

/** t ∈ [0,1]: 0 — мінімум у контексті, 1 — максимум (для витрат «гарячіші» більші суми). */
function pnlNormalizedT(value, min, max) {
    const v = Number(value);
    if (!Number.isFinite(v)) return null;
    const lo = Number(min);
    const hi = Number(max);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;
    return Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
}

function pnlHeatBackgroundColor(t) {
    if (t == null || !Number.isFinite(t)) return undefined;
    const r1 = 236,
        g1 = 248,
        b1 = 255;
    const r2 = 255,
        g2 = 214,
        b2 = 205;
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r}, ${g}, ${b})`;
}

function pnlCombineHeatT(tRow, tCol, heatRows, heatCols) {
    if (!heatRows && !heatCols) return null;
    const parts = [];
    if (heatRows && tRow != null) parts.push(tRow);
    if (heatCols && tCol != null) parts.push(tCol);
    if (parts.length === 0) return null;
    return parts.reduce((s, x) => s + x, 0) / parts.length;
}

function mergePnlCellStyles(...styles) {
    const o = Object.assign({}, ...styles.filter(Boolean));
    return Object.keys(o).length ? o : undefined;
}

/** Сортування рядків категорій / додаткових рядків за сумою в колонці місяця. */
function compareExpenseKeysForMonth(keys, dir, ym, byMonth) {
    const m = byMonth[ym];
    if (!m) return keys;
    const getVal = (key) => {
        const row = m.expenses.find((r) => r.key === key);
        return Number(row?.displayUah ?? 0);
    };
    const mult = dir === "desc" ? -1 : 1;
    return [...keys].sort((a, b) => {
        const d = (getVal(a) - getVal(b)) * mult;
        if (d !== 0) return d;
        return a.localeCompare(b, "uk");
    });
}

function compareCustomIdsForMonth(ids, dir, ym, byMonth) {
    const m = byMonth[ym];
    if (!m) return ids;
    const getVal = (cid) => {
        const line = m.customLines.find((x) => x.id === cid);
        return Number(line?.amountUah ?? 0);
    };
    const mult = dir === "desc" ? -1 : 1;
    return [...ids].sort((a, b) => {
        const d = (getVal(a) - getVal(b)) * mult;
        if (d !== 0) return d;
        return a.localeCompare(b);
    });
}

/** Цілі гривні з пробілами між розрядами (для полів вводу). */
function formatThousandsInt(n) {
    const x = Math.round(Number(n));
    if (!Number.isFinite(x)) return "0";
    return x.toLocaleString("uk-UA", { maximumFractionDigits: 0 });
}

function parseMoneyInput(s) {
    const normalized = String(s || "")
        .replace(/[\s\u00A0\u202F\u2007]/g, "")
        .replace(",", ".");
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
}

/** Як у полі: показ і фокус орієнтовані на Math.round(грн). */
function amountUnchangedForCommit(parsed, valueUah) {
    return Math.round(Number(parsed)) === Math.round(Number(valueUah ?? 0));
}

/**
 * Число в полі: у згорнутому вигляді — 1 234 567; у фокусі — без пробілів для редагування.
 */
function ReadableAmountInput({ valueUah, disabled, title, className, style, onCommit }) {
    const [local, setLocal] = useState(() => formatThousandsInt(valueUah));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) setLocal(formatThousandsInt(valueUah));
    }, [valueUah, focused]);

    return (
        <input
            type="text"
            inputMode="numeric"
            className={className}
            style={{ fontVariantNumeric: "tabular-nums", ...style }}
            value={local}
            disabled={disabled}
            title={title}
            onChange={(e) => setLocal(e.target.value)}
            onFocus={() => {
                setFocused(true);
                setLocal(String(Math.round(Number(valueUah))));
            }}
            onBlur={() => {
                const parsed = parseMoneyInput(local);
                const rounded = Math.round(Number(parsed));
                setFocused(false);
                if (amountUnchangedForCommit(parsed, valueUah)) {
                    setLocal(formatThousandsInt(valueUah));
                    return;
                }
                setLocal(formatThousandsInt(rounded));
                onCommit(rounded);
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.target.blur();
                }
            }}
        />
    );
}

async function fetchPnlRange(fromYm, toYm) {
    const params = new URLSearchParams({ from: fromYm, to: toYm });
    const res = await fetch(`/api/pnl/range?${params}`, { credentials: "include" });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Помилка завантаження P&L");
    }
    return res.json();
}

async function patchPnlMonth(yearMonth, body) {
    const res = await fetch(`/api/pnl/month/${yearMonth}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Помилка збереження");
    }
    return res.json();
}

function firstDayOfYm(ym) {
    if (!ym || typeof ym !== "string" || !/^\d{4}-\d{2}$/.test(ym)) return undefined;
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1);
}

function lastDayOfYm(ym) {
    if (!ym || typeof ym !== "string" || !/^\d{4}-\d{2}$/.test(ym)) return undefined;
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m, 0);
}

const PNL_HL_HEAD = "pnl:head";
const PNL_HL_INCOME = "pnl:income";
const PNL_HL_SECTION_CUSTOM = "pnl:section-custom";
const PNL_HL_ADD_ROW = "pnl:add-row";
const PNL_HL_TOT_EXP = "pnl:tot-exp";
const PNL_HL_TOT_PROFIT = "pnl:tot-profit";
const PNL_HL_TOT_MARGIN = "pnl:tot-margin";

/** Категорії без обороту з банку за місяць — показуємо поле 0 грн, щоб можна було ввести вручну (не «—»). */
const PNL_EXPENSE_KEYS_ALWAYS_EDITABLE = new Set(["Дивіденди"]);

function pnlExpRowKey(categoryKey) {
    return `pnl:exp:${categoryKey}`;
}

function pnlCustRowKey(cid) {
    return `pnl:cust:${cid}`;
}

function pnlHoverClass(rowKey, colIndex, hover) {
    const hR = hover?.row ?? null;
    const hC = hover?.col ?? null;
    if (hR == null && hC == null) return "";
    const rowHit = hR != null && rowKey === hR;
    const colHit = hC != null && colIndex != null && colIndex === hC;
    if (rowHit && colHit) return "pnl-matrix-hl-cross";
    if (rowHit) return "pnl-matrix-hl-row";
    if (colHit) return "pnl-matrix-hl-col";
    return "";
}

function PnlHoverCell({
    as: Tag = "td",
    rowKey,
    colIndex,
    hover,
    onHoverEnter,
    className = "",
    children,
    ...rest
}) {
    const hl = pnlHoverClass(rowKey, colIndex, hover);
    const cn = [className, hl].filter(Boolean).join(" ").trim();
    return (
        <Tag
            className={cn || undefined}
            onMouseEnter={() => onHoverEnter(rowKey, colIndex)}
            {...rest}
        >
            {children}
        </Tag>
    );
}

function MonthCellInput({
    valueUah,
    fromTxUah,
    syncFromTransactions,
    locked,
    disabled,
    title,
    onCommit,
}) {
    const handleParsedCommit = (parsed) => {
        const tx = fromTxUah ?? 0;
        const shouldLock = syncFromTransactions
            ? Math.abs(parsed - tx) >= 0.01
            : Math.abs(parsed) >= 0.01;
        onCommit({ parsed, shouldLock });
    };

    const fromTransactions = syncFromTransactions && !locked;
    const sourceLabel = fromTransactions
        ? "З транзакцій"
        : locked
          ? "Вручну"
          : "Без підтягування з банку";
    const iconTitle = title ? `${sourceLabel}. ${title}` : sourceLabel;

    return (
        <div className="position-relative w-100">
            {fromTransactions ? (
                <span
                    className="position-absolute d-inline-flex align-items-center justify-content-center text-primary"
                    title={iconTitle}
                    aria-label={sourceLabel}
                    style={{
                        right: "0.35rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "1.1rem",
                        height: "1.1rem",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    <CurrencyExchange size={16} className="d-block flex-shrink-0" aria-hidden />
                </span>
            ) : null}
            <ReadableAmountInput
                valueUah={valueUah}
                disabled={disabled}
                title={iconTitle}
                className="form-control form-control-sm text-end pnl-matrix-amount-input"
                style={{
                    minWidth: "6.5rem",
                    paddingRight: fromTransactions ? "1.85rem" : undefined,
                }}
                onCommit={handleParsedCommit}
            />
        </div>
    );
}

export default function PnlPage() {
    const toYmDefault = dayjs().format("YYYY-MM");
    const fromYmDefault = dayjs().subtract(11, "month").format("YYYY-MM");

    const [fromYm, setFromYm] = useState(fromYmDefault);
    const [toYm, setToYm] = useState(toYmDefault);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [pnlHover, setPnlHover] = useState({ row: null, col: null });
    /** Показ суми в гривнях і відсотків у комірках (вимкніть для скринів тощо). */
    const [showValues, setShowValues] = useState(true);
    const [showPctOfIncome, setShowPctOfIncome] = useState(false);
    /** Сортування матриці за сумою в обраному місяці (рядки витрат і додаткові). */
    const [columnSort, setColumnSort] = useState(null);
    /** Підсвітка min/max у рядку (місяці) та/або у стовпці (категорії витрат за місяць). */
    const [pnlHeatmapRows, setPnlHeatmapRows] = useState(true);
    const [pnlHeatmapCols, setPnlHeatmapCols] = useState(true);

    const onPnlHoverEnter = useCallback((rowKey, colIndex) => {
        setPnlHover({ row: rowKey, col: colIndex });
    }, []);

    const onPnlTableMouseLeave = useCallback(() => {
        setPnlHover({ row: null, col: null });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const json = await fetchPnlRange(fromYm, toYm);
            setData(json);
        } catch (e) {
            console.error(e);
            setError(e.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, [fromYm, toYm]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setColumnSort(null);
    }, [fromYm, toYm]);

    const months = data?.months || [];

    const toggleColumnSort = useCallback((ym) => {
        setColumnSort((prev) => {
            if (!prev || prev.ym !== ym) return { ym, dir: "desc" };
            if (prev.dir === "desc") return { ym, dir: "asc" };
            return null;
        });
    }, []);

    const sortableThProps = useCallback(
        (ym) => ({
            role: "button",
            tabIndex: 0,
            title: "Сортувати категорії за цією колонкою (спадання → зростання → типовий порядок)",
            onClick: (e) => {
                e.preventDefault();
                toggleColumnSort(ym);
            },
            onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleColumnSort(ym);
                }
            },
        }),
        [toggleColumnSort]
    );

    const saveExpenseCell = useCallback(
        async (yearMonth, key, parsedUah, shouldLock) => {
            setSaving(true);
            setError(null);
            try {
                await patchPnlMonth(yearMonth, {
                    expensePatches: [{ key, amountUah: parsedUah, locked: shouldLock }],
                });
                await load();
            } catch (e) {
                console.error(e);
                setError(e.message || "Помилка збереження");
            } finally {
                setSaving(false);
            }
        },
        [load]
    );

    const saveIncomeCell = useCallback(
        async (yearMonth, parsedUah, shouldLock) => {
            setSaving(true);
            setError(null);
            try {
                await patchPnlMonth(yearMonth, {
                    incomeUah: parsedUah,
                    incomeLocked: shouldLock,
                });
                await load();
            } catch (e) {
                console.error(e);
                setError(e.message || "Помилка збереження");
            } finally {
                setSaving(false);
            }
        },
        [load]
    );

    const saveCustomLine = useCallback(
        async (yearMonth, allCustomLinesForMonth) => {
            setSaving(true);
            setError(null);
            try {
                await patchPnlMonth(yearMonth, { customLines: allCustomLinesForMonth });
                await load();
            } catch (e) {
                console.error(e);
                setError(e.message || "Помилка збереження");
            } finally {
                setSaving(false);
            }
        },
        [load]
    );

    const addCustomRow = async () => {
        const id = crypto.randomUUID();
        const label = "Додатковий рядок";
        if (!data || !months.length) return;
        setSaving(true);
        setError(null);
        try {
            await Promise.all(
                months.map(async (ym) => {
                    const prev = data.byMonth[ym].customLines || [];
                    await patchPnlMonth(ym, { customLines: [...prev, { id, label, amountUah: 0 }] });
                })
            );
            await load();
        } catch (e) {
            console.error(e);
            setError(e.message || "Помилка");
        } finally {
            setSaving(false);
        }
    };

    const expenseKeys = data?.expenseKeysOrdered || [];

    const sortedExpenseKeys = useMemo(() => {
        if (!columnSort || !data?.byMonth) return expenseKeys;
        const { ym, dir } = columnSort;
        if (!months.includes(ym)) return expenseKeys;
        return compareExpenseKeysForMonth(expenseKeys, dir, ym, data.byMonth);
    }, [expenseKeys, data, columnSort, months]);

    const sortedCustomIds = useMemo(() => {
        const base = data?.customIdsOrdered || [];
        if (!columnSort || !data?.byMonth) return base;
        const { ym, dir } = columnSort;
        if (!months.includes(ym)) return base;
        return compareCustomIdsForMonth(base, dir, ym, data.byMonth);
    }, [data, columnSort, months]);

    const monthMode = useMemo(
        () => pnlMonthViewMode(showValues, showPctOfIncome),
        [showValues, showPctOfIncome]
    );

    const pnlHeatRowBounds = useMemo(() => {
        if (!data?.byMonth || !months.length) return new Map();
        const map = new Map();
        for (const key of sortedExpenseKeys) {
            let min = Infinity;
            let max = -Infinity;
            for (const ym of months) {
                const r = data.byMonth[ym].expenses.find((e) => e.key === key);
                const v = r ? r.displayUah : 0;
                min = Math.min(min, v);
                max = Math.max(max, v);
            }
            if (min === Infinity) min = 0;
            if (max === -Infinity) max = 0;
            map.set(key, { min, max });
        }
        for (const cid of sortedCustomIds) {
            let min = Infinity;
            let max = -Infinity;
            for (const ym of months) {
                const line = data.byMonth[ym].customLines.find((x) => x.id === cid);
                const v = line ? line.amountUah : 0;
                min = Math.min(min, v);
                max = Math.max(max, v);
            }
            if (min === Infinity) min = 0;
            if (max === -Infinity) max = 0;
            map.set(`__cust__${cid}`, { min, max });
        }
        return map;
    }, [data, months, sortedExpenseKeys, sortedCustomIds]);

    const pnlHeatColBounds = useMemo(() => {
        if (!data?.byMonth || !months.length) return new Map();
        const map = new Map();
        for (const ym of months) {
            const vals = [];
            for (const key of sortedExpenseKeys) {
                const r = data.byMonth[ym].expenses.find((e) => e.key === key);
                vals.push(r ? r.displayUah : 0);
            }
            for (const cid of sortedCustomIds) {
                const line = data.byMonth[ym].customLines.find((x) => x.id === cid);
                vals.push(line ? line.amountUah : 0);
            }
            if (vals.length === 0) {
                map.set(ym, { min: 0, max: 0 });
                continue;
            }
            map.set(ym, { min: Math.min(...vals), max: Math.max(...vals) });
        }
        return map;
    }, [data, months, sortedExpenseKeys, sortedCustomIds]);

    const pnlHeatStyleUah = useCallback(
        (uah, rowBoundsKey, ym) => {
            if (!pnlHeatmapRows && !pnlHeatmapCols) return undefined;
            const bRow = pnlHeatRowBounds.get(rowBoundsKey);
            const bCol = pnlHeatColBounds.get(ym);
            const tRow =
                pnlHeatmapRows && bRow ? pnlNormalizedT(uah, bRow.min, bRow.max) : null;
            const tCol =
                pnlHeatmapCols && bCol ? pnlNormalizedT(uah, bCol.min, bCol.max) : null;
            const t = pnlCombineHeatT(tRow, tCol, pnlHeatmapRows, pnlHeatmapCols);
            const bg = pnlHeatBackgroundColor(t);
            return bg ? { backgroundColor: bg } : undefined;
        },
        [pnlHeatmapRows, pnlHeatmapCols, pnlHeatRowBounds, pnlHeatColBounds]
    );

    const summaryByMonth = useMemo(() => {
        const out = {};
        if (!data?.byMonth) return out;
        for (const ym of months) {
            out[ym] = data.byMonth[ym]?.totals || {};
        }
        return out;
    }, [data, months]);

    const tableDataColSpan = 1 + months.length * colsPerMonthForMode(monthMode);

    return (
        <div className="p-3">
            <div className="mb-2">
                <h1 className="h4 mb-0">{"P&L"}</h1>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div style={{ width: "14rem" }} className="flex-shrink-0">
                    <AppMonthField
                        value={fromYm}
                        size="sm"
                        className="w-100"
                        disabled={loading}
                        maxDate={lastDayOfYm(toYm)}
                        onChange={(v) => {
                            if (!v) return;
                            setFromYm(v);
                            if (v > toYm) setToYm(v);
                        }}
                    />
                </div>
                <div style={{ width: "14rem" }} className="flex-shrink-0">
                    <AppMonthField
                        value={toYm}
                        size="sm"
                        className="w-100"
                        disabled={loading}
                        minDate={firstDayOfYm(fromYm)}
                        onChange={(v) => {
                            if (!v) return;
                            setToYm(v);
                            if (v < fromYm) setFromYm(v);
                        }}
                    />
                </div>
                <AppCheckbox
                    id="pnl-show-values"
                    label="Суми"
                    className="mb-0 flex-shrink-0"
                    checked={showValues}
                    onChange={(e) => setShowValues(e.target.checked)}
                    disabled={loading}
                    title="Лише суми в гривнях (одна числова колонка на місяць). Разом з «%» — обидві колонки. Якщо вимкнути обидва — у місяцях лише прочерки"
                />
                <AppCheckbox
                    id="pnl-show-pct-of-income"
                    label="%"
                    className="mb-0 flex-shrink-0"
                    checked={showPctOfIncome}
                    onChange={(e) => setShowPctOfIncome(e.target.checked)}
                    disabled={loading}
                    title="Лише частки від доходу (одна колонка на місяць). Разом з «Суми» — числа й %. Якщо «Суми» вимкнено — у таблиці тільки %"
                />
                <AppCheckbox
                    id="pnl-heatmap-rows"
                    label="Тепло: рядки"
                    className="mb-0 flex-shrink-0"
                    checked={pnlHeatmapRows}
                    onChange={(e) => setPnlHeatmapRows(e.target.checked)}
                    disabled={loading}
                    title="Підсвітка комірок: у межах категорії порівняння сум по місяцях (холодніший — найменша витрата за рядком, тепліший — найбільша)"
                />
                <AppCheckbox
                    id="pnl-heatmap-cols"
                    label="Тепло: місяці"
                    className="mb-0 flex-shrink-0"
                    checked={pnlHeatmapCols}
                    onChange={(e) => setPnlHeatmapCols(e.target.checked)}
                    disabled={loading}
                    title="Підсвітка комірок: у межах місяця порівняння категорій витрат і додаткових рядків (найвища витрата за місяць — тепліше)"
                />
                {saving && <span className="small text-muted">Зберігання…</span>}
            </div>

            {error && (
                <div className="alert alert-danger py-2" role="alert">
                    {error}
                </div>
            )}

            {loading || !data ? (
                <Loader />
            ) : (
                <div
                    className="table-responsive border rounded"
                    style={{ maxHeight: "80vh" }}
                    onMouseLeave={onPnlTableMouseLeave}
                >
                    <table className="table table-sm table-bordered align-middle mb-0 pnl-matrix-table">
                        <thead className="table-light sticky-top" style={{ zIndex: 2 }}>
                            <tr>
                                <PnlHoverCell
                                    as="th"
                                    rowKey={PNL_HL_HEAD}
                                    colIndex={0}
                                    hover={pnlHover}
                                    onHoverEnter={onPnlHoverEnter}
                                    className="sticky-start bg-light"
                                    style={{
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 3,
                                        minWidth: "14rem",
                                    }}
                                >
                                    Показник
                                </PnlHoverCell>
                                {months.map((ym, mi) => {
                                    const active = columnSort?.ym === ym;
                                    const ariaSort = active
                                        ? columnSort.dir === "desc"
                                            ? "descending"
                                            : "ascending"
                                        : "none";
                                    const cAmt = pnlMonthColAmount(mi, monthMode);
                                    const cPct = pnlMonthColPct(mi, monthMode);
                                    const sortArrow = active ? (
                                        <span className="pnl-col-sort-arrow" aria-hidden>
                                            {columnSort.dir === "desc" ? "↓" : "↑"}
                                        </span>
                                    ) : null;
                                    return (
                                        <React.Fragment key={ym}>
                                            {cAmt != null ? (
                                                <PnlHoverCell
                                                    as="th"
                                                    rowKey={PNL_HL_HEAD}
                                                    colIndex={cAmt}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-center text-nowrap small pnl-col-sort-th"
                                                    aria-sort={ariaSort}
                                                    {...sortableThProps(ym)}
                                                >
                                                    <span className="pnl-col-sort-th-inner d-inline-flex align-items-center justify-content-center gap-1">
                                                        <span>{dayjs(`${ym}-01`).format("MMM YYYY")}</span>
                                                        {sortArrow}
                                                    </span>
                                                </PnlHoverCell>
                                            ) : null}
                                            {monthMode === "both" && cPct != null ? (
                                                <PnlHoverCell
                                                    as="th"
                                                    rowKey={PNL_HL_HEAD}
                                                    colIndex={cPct}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-center text-nowrap small text-muted pnl-col-sort-th"
                                                    aria-sort={ariaSort}
                                                    {...sortableThProps(ym)}
                                                >
                                                    <span className="pnl-col-sort-th-inner d-inline-flex align-items-center justify-content-center gap-1">
                                                        <span>%</span>
                                                        {sortArrow}
                                                    </span>
                                                </PnlHoverCell>
                                            ) : null}
                                            {monthMode === "pct" && cPct != null ? (
                                                <PnlHoverCell
                                                    as="th"
                                                    rowKey={PNL_HL_HEAD}
                                                    colIndex={cPct}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-center text-nowrap small pnl-col-sort-th"
                                                    aria-sort={ariaSort}
                                                    {...sortableThProps(ym)}
                                                >
                                                    <span className="pnl-col-sort-th-inner d-inline-flex align-items-center justify-content-center gap-1">
                                                        <span>{dayjs(`${ym}-01`).format("MMM YYYY")}</span>
                                                        <span className="text-muted small">%</span>
                                                        {sortArrow}
                                                    </span>
                                                </PnlHoverCell>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-primary">
                                <PnlHoverCell
                                    rowKey={PNL_HL_INCOME}
                                    colIndex={0}
                                    hover={pnlHover}
                                    onHoverEnter={onPnlHoverEnter}
                                    className="fw-semibold sticky-start bg-primary-subtle"
                                    style={{ position: "sticky", left: 0, zIndex: 1 }}
                                >
                                    Доходи
                                </PnlHoverCell>
                                {months.map((ym, mi) => {
                                    const m = data.byMonth[ym];
                                    const inc = m.income.displayUah;
                                    const cAmt = pnlMonthColAmount(mi, monthMode);
                                    const cPct = pnlMonthColPct(mi, monthMode);
                                    return (
                                        <React.Fragment key={ym}>
                                            {cAmt != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_INCOME}
                                                    colIndex={cAmt}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="bg-primary-subtle"
                                                >
                                                    {monthMode === "both" || monthMode === "sums" ? (
                                                        <MonthCellInput
                                                            valueUah={m.income.displayUah}
                                                            fromTxUah={m.income.fromTxUah}
                                                            syncFromTransactions={m.syncFromTransactions}
                                                            locked={m.income.locked}
                                                            disabled={saving}
                                                            title={`З транзакцій: ${formatMoney(m.income.fromTxUah)} грн`}
                                                            onCommit={({ parsed, shouldLock }) => {
                                                                saveIncomeCell(ym, parsed, shouldLock);
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted text-end d-block px-2 py-1">
                                                            —
                                                        </span>
                                                    )}
                                                </PnlHoverCell>
                                            ) : null}
                                            {cPct != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_INCOME}
                                                    colIndex={cPct}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="bg-primary-subtle text-end small text-muted"
                                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                                    title="Частка від доходу"
                                                >
                                                    {monthMode === "both" || monthMode === "pct"
                                                        ? formatPctOfIncome(inc, inc)
                                                        : "—"}
                                                </PnlHoverCell>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>

                            {sortedExpenseKeys.map((key) => {
                                const rk = pnlExpRowKey(key);
                                return (
                                    <tr key={key}>
                                        <PnlHoverCell
                                            rowKey={rk}
                                            colIndex={0}
                                            hover={pnlHover}
                                            onHoverEnter={onPnlHoverEnter}
                                            className="sticky-start bg-white"
                                            style={{ position: "sticky", left: 0, zIndex: 1 }}
                                        >
                                            {key}
                                        </PnlHoverCell>
                                        {months.map((ym, mi) => {
                                            const m = data.byMonth[ym];
                                            const inc = m.income.displayUah;
                                            const row = m.expenses.find((r) => r.key === key);
                                            const cAmt = pnlMonthColAmount(mi, monthMode);
                                            const cPct = pnlMonthColPct(mi, monthMode);
                                            if (!row) {
                                                const editableZero =
                                                    PNL_EXPENSE_KEYS_ALWAYS_EDITABLE.has(key) &&
                                                    (monthMode === "both" || monthMode === "sums");
                                                const pctWhenNoRow =
                                                    PNL_EXPENSE_KEYS_ALWAYS_EDITABLE.has(key) &&
                                                    (monthMode === "both" || monthMode === "pct");
                                                const uahEmpty = 0;
                                                const heatEmpty = pnlHeatStyleUah(uahEmpty, key, ym);
                                                return (
                                                    <React.Fragment key={ym}>
                                                        {cAmt != null ? (
                                                            <PnlHoverCell
                                                                rowKey={rk}
                                                                colIndex={cAmt}
                                                                hover={pnlHover}
                                                                onHoverEnter={onPnlHoverEnter}
                                                                style={mergePnlCellStyles(heatEmpty)}
                                                            >
                                                                {editableZero ? (
                                                                    <MonthCellInput
                                                                        valueUah={0}
                                                                        fromTxUah={0}
                                                                        syncFromTransactions={
                                                                            m.syncFromTransactions
                                                                        }
                                                                        locked={false}
                                                                        disabled={saving}
                                                                        title="Немає руху за категорією за місяць — введіть вручну"
                                                                        onCommit={({ parsed, shouldLock }) => {
                                                                            saveExpenseCell(
                                                                                ym,
                                                                                key,
                                                                                parsed,
                                                                                shouldLock
                                                                            );
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </PnlHoverCell>
                                                        ) : null}
                                                        {cPct != null ? (
                                                            <PnlHoverCell
                                                                rowKey={rk}
                                                                colIndex={cPct}
                                                                hover={pnlHover}
                                                                onHoverEnter={onPnlHoverEnter}
                                                                className="text-end small text-muted"
                                                                style={mergePnlCellStyles(heatEmpty, {
                                                                    fontVariantNumeric: "tabular-nums",
                                                                })}
                                                                title="Частка від доходу"
                                                            >
                                                                {pctWhenNoRow
                                                                    ? formatPctOfIncome(0, inc)
                                                                    : "—"}
                                                            </PnlHoverCell>
                                                        ) : null}
                                                    </React.Fragment>
                                                );
                                            }
                                            const uahRow = row.displayUah;
                                            const heatRow = pnlHeatStyleUah(uahRow, key, ym);
                                            return (
                                                <React.Fragment key={ym}>
                                                    {cAmt != null ? (
                                                        <PnlHoverCell
                                                            rowKey={rk}
                                                            colIndex={cAmt}
                                                            hover={pnlHover}
                                                            onHoverEnter={onPnlHoverEnter}
                                                            style={mergePnlCellStyles(heatRow)}
                                                        >
                                                            {monthMode === "both" || monthMode === "sums" ? (
                                                                <MonthCellInput
                                                                    valueUah={row.displayUah}
                                                                    fromTxUah={row.fromTxUah}
                                                                    syncFromTransactions={m.syncFromTransactions}
                                                                    locked={row.locked}
                                                                    disabled={saving}
                                                                    title={`Транзакцій: ${row.fromTxCount}, сума з банку: ${formatMoney(row.fromTxUah)} грн`}
                                                                    onCommit={({ parsed, shouldLock }) => {
                                                                        saveExpenseCell(ym, key, parsed, shouldLock);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted text-end d-block px-2 py-1">
                                                                    —
                                                                </span>
                                                            )}
                                                        </PnlHoverCell>
                                                    ) : null}
                                                    {cPct != null ? (
                                                        <PnlHoverCell
                                                            rowKey={rk}
                                                            colIndex={cPct}
                                                            hover={pnlHover}
                                                            onHoverEnter={onPnlHoverEnter}
                                                            className="text-end small text-muted"
                                                            style={mergePnlCellStyles(heatRow, {
                                                                fontVariantNumeric: "tabular-nums",
                                                            })}
                                                            title="Частка від доходу"
                                                        >
                                                            {monthMode === "both" || monthMode === "pct"
                                                                ? formatPctOfIncome(row.displayUah, inc)
                                                                : "—"}
                                                        </PnlHoverCell>
                                                    ) : null}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                );
                            })}

                            {(sortedCustomIds || []).length > 0 && (
                                <tr>
                                    <PnlHoverCell
                                        rowKey={PNL_HL_SECTION_CUSTOM}
                                        colIndex={null}
                                        colSpan={tableDataColSpan}
                                        hover={pnlHover}
                                        onHoverEnter={onPnlHoverEnter}
                                        className="small text-muted fw-semibold bg-body-secondary"
                                    >
                                        Додаткові рядки
                                    </PnlHoverCell>
                                </tr>
                            )}

                            {(sortedCustomIds || []).map((cid) => {
                                const rk = pnlCustRowKey(cid);
                                const label =
                                    months
                                        .map((ym) => {
                                            const l = data.byMonth[ym].customLines.find((x) => x.id === cid);
                                            return l?.label;
                                        })
                                        .find(Boolean) || cid.slice(0, 8);
                                return (
                                    <tr key={cid}>
                                        <PnlHoverCell
                                            rowKey={rk}
                                            colIndex={0}
                                            hover={pnlHover}
                                            onHoverEnter={onPnlHoverEnter}
                                            className="sticky-start bg-white"
                                            style={{ position: "sticky", left: 0, zIndex: 1 }}
                                        >
                                            {label}
                                        </PnlHoverCell>
                                        {months.map((ym, mi) => {
                                            const m = data.byMonth[ym];
                                            const inc = m.income.displayUah;
                                            const lines = m.customLines || [];
                                            let line = lines.find((x) => x.id === cid);
                                            if (!line) {
                                                line = { id: cid, label, amountUah: 0 };
                                            }
                                            const cAmt = pnlMonthColAmount(mi, monthMode);
                                            const cPct = pnlMonthColPct(mi, monthMode);
                                            const heatCust = pnlHeatStyleUah(
                                                line.amountUah,
                                                `__cust__${cid}`,
                                                ym
                                            );
                                            return (
                                                <React.Fragment key={ym}>
                                                    {cAmt != null ? (
                                                        <PnlHoverCell
                                                            rowKey={rk}
                                                            colIndex={cAmt}
                                                            hover={pnlHover}
                                                            onHoverEnter={onPnlHoverEnter}
                                                            style={mergePnlCellStyles(heatCust)}
                                                        >
                                                            {monthMode === "both" || monthMode === "sums" ? (
                                                                <ReadableAmountInput
                                                                    valueUah={line.amountUah}
                                                                    disabled={saving}
                                                                    title="Додано вручну"
                                                                    className="form-control form-control-sm text-end flex-grow-1 pnl-matrix-amount-input"
                                                                    style={{ minWidth: "6.5rem" }}
                                                                    onCommit={(parsed) => {
                                                                        const next = lines.filter((x) => x.id !== cid);
                                                                        next.push({
                                                                            ...line,
                                                                            amountUah: parsed,
                                                                        });
                                                                        saveCustomLine(ym, next);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted text-end d-block px-2 py-1">
                                                                    —
                                                                </span>
                                                            )}
                                                        </PnlHoverCell>
                                                    ) : null}
                                                    {cPct != null ? (
                                                        <PnlHoverCell
                                                            rowKey={rk}
                                                            colIndex={cPct}
                                                            hover={pnlHover}
                                                            onHoverEnter={onPnlHoverEnter}
                                                            className="text-end small text-muted"
                                                            style={mergePnlCellStyles(heatCust, {
                                                                fontVariantNumeric: "tabular-nums",
                                                            })}
                                                            title="Частка від доходу"
                                                        >
                                                            {monthMode === "both" || monthMode === "pct"
                                                                ? formatPctOfIncome(line.amountUah, inc)
                                                                : "—"}
                                                        </PnlHoverCell>
                                                    ) : null}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                );
                            })}

                            <tr>
                                <PnlHoverCell
                                    rowKey={PNL_HL_ADD_ROW}
                                    colIndex={null}
                                    className="border-0 text-start"
                                    colSpan={tableDataColSpan}
                                    hover={pnlHover}
                                    onHoverEnter={onPnlHoverEnter}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary px-2"
                                        onClick={addCustomRow}
                                        disabled={saving || !months.length}
                                        title="Додати рядок (усі місяці в діапазоні)"
                                        aria-label="Додати рядок (усі місяці в діапазоні)"
                                    >
                                        +
                                    </button>
                                </PnlHoverCell>
                            </tr>

                            <tr className="table-light fw-semibold">
                                <PnlHoverCell
                                    rowKey={PNL_HL_TOT_EXP}
                                    colIndex={0}
                                    hover={pnlHover}
                                    onHoverEnter={onPnlHoverEnter}
                                    className="sticky-start bg-light"
                                    style={{ position: "sticky", left: 0, zIndex: 1 }}
                                >
                                    Усього витрат
                                </PnlHoverCell>
                                {months.map((ym, mi) => {
                                    const m = data.byMonth[ym];
                                    const inc = m.income.displayUah;
                                    const tot = summaryByMonth[ym]?.totalExpensesUah;
                                    const cAmt = pnlMonthColAmount(mi, monthMode);
                                    const cPct = pnlMonthColPct(mi, monthMode);
                                    return (
                                        <React.Fragment key={ym}>
                                            {cAmt != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_TOT_EXP}
                                                    colIndex={cAmt}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-end"
                                                >
                                                    {monthMode === "both" || monthMode === "sums"
                                                        ? `${formatMoney(tot)} грн`
                                                        : "—"}
                                                </PnlHoverCell>
                                            ) : null}
                                            {cPct != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_TOT_EXP}
                                                    colIndex={cPct}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-end small text-muted"
                                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                                    title="Частка від доходу"
                                                >
                                                    {monthMode === "both" || monthMode === "pct"
                                                        ? formatPctOfIncome(tot, inc)
                                                        : "—"}
                                                </PnlHoverCell>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                            <tr className="fw-semibold">
                                <PnlHoverCell
                                    rowKey={PNL_HL_TOT_PROFIT}
                                    colIndex={0}
                                    hover={pnlHover}
                                    onHoverEnter={onPnlHoverEnter}
                                    className="sticky-start bg-white"
                                    style={{ position: "sticky", left: 0, zIndex: 1 }}
                                >
                                    Чистий результат
                                </PnlHoverCell>
                                {months.map((ym, mi) => {
                                    const m = data.byMonth[ym];
                                    const inc = m.income.displayUah;
                                    const profit = summaryByMonth[ym]?.profitUah;
                                    const cAmt = pnlMonthColAmount(mi, monthMode);
                                    const cPct = pnlMonthColPct(mi, monthMode);
                                    return (
                                        <React.Fragment key={ym}>
                                            {cAmt != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_TOT_PROFIT}
                                                    colIndex={cAmt}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-end"
                                                >
                                                    {monthMode === "both" || monthMode === "sums"
                                                        ? `${formatMoney(profit)} грн`
                                                        : "—"}
                                                </PnlHoverCell>
                                            ) : null}
                                            {cPct != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_TOT_PROFIT}
                                                    colIndex={cPct}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-end small text-muted"
                                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                                    title="Частка від доходу"
                                                >
                                                    {monthMode === "both" || monthMode === "pct"
                                                        ? formatPctOfIncome(profit, inc)
                                                        : "—"}
                                                </PnlHoverCell>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                            <tr className="text-danger fw-semibold">
                                <PnlHoverCell
                                    rowKey={PNL_HL_TOT_MARGIN}
                                    colIndex={0}
                                    hover={pnlHover}
                                    onHoverEnter={onPnlHoverEnter}
                                    className="sticky-start bg-white"
                                    style={{ position: "sticky", left: 0, zIndex: 1 }}
                                >
                                    Маржа, %
                                </PnlHoverCell>
                                {months.map((ym, mi) => {
                                    const p = summaryByMonth[ym]?.profitPct;
                                    const cAmt = pnlMonthColAmount(mi, monthMode);
                                    const cPct = pnlMonthColPct(mi, monthMode);
                                    return (
                                        <React.Fragment key={ym}>
                                            {cAmt != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_TOT_MARGIN}
                                                    colIndex={cAmt}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-end"
                                                >
                                                    {monthMode === "neither"
                                                        ? "—"
                                                        : p == null
                                                          ? "—"
                                                          : `${p} %`}
                                                </PnlHoverCell>
                                            ) : null}
                                            {cPct != null ? (
                                                <PnlHoverCell
                                                    rowKey={PNL_HL_TOT_MARGIN}
                                                    colIndex={cPct}
                                                    hover={pnlHover}
                                                    onHoverEnter={onPnlHoverEnter}
                                                    className="text-end small text-muted"
                                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                                    title="Вже у відсотках від доходу"
                                                >
                                                    {monthMode === "both"
                                                        ? "—"
                                                        : monthMode === "pct"
                                                          ? p == null
                                                              ? "—"
                                                              : `${p} %`
                                                          : "—"}
                                                </PnlHoverCell>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
